import {
  Box,
  Checkbox,
  type CheckboxGroupProps,
  Divider,
  Flex,
  Loader,
  Text,
} from "@mantine/core";
import { type ReactNode } from "react";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";
import { SectionDivider } from "src/styled";

import { useTheme } from "../../../../hooks";
import CheckboxGroupItem from "./CheckboxGroupItem";
import { collectPartValues } from "./checkboxGroup.utils";
import useCheckboxGroupData from "./useCheckboxGroupData";
import SearchInput from "../../LocalSearch/SearchInput";
import type { ComboboxItem } from "@mantine/core";
import styles from "./CheckboxGroup.module.scss";

interface Props<T extends FieldValues, K extends ComboboxItem> {
  title?: string;
  data: K[];
  renderLabel?: (item: K) => ReactNode;
  name: Path<T>;
  control: Control<T>;
  checkboxGroupProps?: Omit<CheckboxGroupProps, "children">;
  scrollHeight?: number | string;
  error?: boolean;
  children?: ReactNode;
  disabled?: boolean;
  grouped?: boolean;
  searchable?: boolean;
  full?: boolean;
  loading?: boolean;
  noScroll?: boolean;
  scrollAreaStyle?: React.CSSProperties;
  searchContainerStyle?: React.CSSProperties;
}

function CheckboxGroup<T extends FieldValues, K extends ComboboxItem>({
  title,
  data,
  name,
  control,
  checkboxGroupProps,
  scrollHeight,
  error,
  children,
  grouped,
  searchable,
  full,
  loading,
  noScroll,
  scrollAreaStyle,
  searchContainerStyle,
  ...itemProps
}: Props<T, K>) {
  const theme = useTheme();
  const {
    field: { value = [], onChange },
  } = useController({
    name,
    control,
  });

  const { groupedData, ungroupedData, searchStr, setSearchStr } =
    useCheckboxGroupData({ data, grouped });

  const isGroupSelected = (groupItems: K[]) => {
    return groupItems.every(item => (value as string[]).includes(item.value));
  };

  const isGroupPartiallySelected = (groupItems: K[]) => {
    return (
      groupItems.some(item => (value as string[]).includes(item.value)) &&
      !isGroupSelected(groupItems)
    );
  };

  const handleGroupToggle = (groupItems: K[], isSelected: boolean) => {
    const selected = [...(value as string[])];

    groupItems.forEach(item => {
      if (isSelected && !selected.includes(item.value)) {
        selected.push(item.value);
      } else if (!isSelected && selected.includes(item.value)) {
        selected.splice(selected.indexOf(item.value), 1);
      }
    });

    onChange(selected);
  };

  const handleChange = (v: string[]) => {
    const selected = [...v];

    const findItemByValue = (
      items: K[],
      targetValue: string,
      visited = new Set<string>(),
    ): K | null => {
      for (const currentItem of items) {
        if (visited.has(currentItem.value)) continue;

        if (currentItem.value === targetValue) {
          return currentItem;
        }

        if (currentItem.parts?.length) {
          const nextVisited = new Set(visited);
          nextVisited.add(currentItem.value);

          const foundChild = findItemByValue(
            currentItem.parts as K[],
            targetValue,
            nextVisited,
          );
          if (foundChild) return foundChild;
        }
      }

      return null;
    };

    const collectPartValuesForItem = (
      item: K,
      visited = new Set<string>(),
    ): string[] => collectPartValues(item, visited);

    const toggleParts = (lbid: string, clear: boolean = false) => {
      const selectedItem = findItemByValue(data, lbid);
      if (!selectedItem) return;

      const partValues = collectPartValuesForItem(selectedItem);

      partValues.forEach(partValue => {
        if (!clear) {
          if (!selected.includes(partValue)) selected.push(partValue);
        } else {
          if (selected.includes(partValue))
            selected.splice(selected.indexOf(partValue), 1);
        }
      });
    };

    const positiveDiff = v.filter(x => !(value as string[]).includes(x));
    positiveDiff.forEach(lbid => toggleParts(lbid));
    const negativeDiff = (value as string[]).filter(x => !selected.includes(x));

    if (negativeDiff.length > 0) {
      negativeDiff.forEach(lbid => toggleParts(lbid, true));
    }
    onChange(selected);
  };

  const searchInScroll = Boolean(noScroll && searchable);

  const searchElement = searchable ? (
    <SearchInput
      value={searchStr}
      onChange={setSearchStr}
      fullWidth={Boolean(searchable || full)}
      containerStyles={{
        ...(searchInScroll
          ? {
              width: "100%",
              paddingLeft: 8,
              margin: 0,
            }
          : noScroll
            ? {
                flex: "none",
                flexShrink: 0,
                alignSelf: "stretch",
                width: "100%",
                paddingTop: 0,
                paddingBottom: 8,
                paddingLeft: 8,
                margin: 0,
              }
            : {
                flex: 1,
                alignSelf: "stretch",
                width: full ? "100%" : undefined,
                paddingLeft: 8,
              }),
        ...searchContainerStyle,
      }}
    />
  ) : null;

  const listContent = loading ? (
    <Flex
      justify="center"
      mt={10}
      style={{ height: noScroll ? "100%" : scrollHeight }}>
      <Loader />
    </Flex>
  ) : grouped ? (
    Object.entries(groupedData).map(([key, group]) => (
      <div key={key} style={{ overflowX: "hidden" }}>
        <Divider
          my="xs"
          label={
            <Box
              style={{
                textTransform: "capitalize",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              ml={5}>
              <Checkbox
                checked={isGroupSelected(group)}
                indeterminate={isGroupPartiallySelected(group)}
                onChange={event => {
                  event.stopPropagation();
                  handleGroupToggle(group, event.currentTarget.checked);
                }}
                size="sm"
              />
              <Text size="md" c="dimmed">
                {key == "ungrouped" ? "Logbooks" : key}
              </Text>
            </Box>
          }
          labelPosition="left"
        />
        <Checkbox.Group
          value={value}
          onChange={handleChange}
          {...checkboxGroupProps}>
          {group.map(item => (
            <CheckboxGroupItem
              key={item.value}
              {...itemProps}
              item={item}
              selectedValues={value as string[]}
              wrapperStyles={{
                marginLeft: "20px",
                borderLeft: "2px solid",
                borderColor: theme.colors.main[2],
                ...itemProps.wrapperStyles,
              }}
              labelStyles={{
                padding: "0px 10px",
                margin: "0px 20px",
                ...itemProps.labelStyles,
              }}
            />
          ))}
        </Checkbox.Group>
      </div>
    ))
  ) : (
    <Checkbox.Group
      value={value}
      onChange={handleChange}
      {...checkboxGroupProps}>
      {ungroupedData.map(item => (
        <CheckboxGroupItem
          key={item.value}
          {...itemProps}
          item={item}
          selectedValues={value as string[]}
        />
      ))}
    </Checkbox.Group>
  );

  return (
    <div
      className={`${styles.root} ${noScroll ? styles.rootNoScroll : ""} ${searchInScroll ? styles.rootWithStickySearch : ""}`}>
      {title && (
        <SectionDivider mb={15} error={error}>
          {title}
        </SectionDivider>
      )}
      {children}
      {!searchInScroll && searchElement}
      <div
        className={styles.nativeScroll}
        style={{
          ...(noScroll
            ? { flex: 1, minHeight: 0, maxWidth: "100%" }
            : scrollHeight
              ? { height: scrollHeight }
              : undefined),
          ...scrollAreaStyle,
        }}>
        {searchInScroll && (
          <div className={styles.searchInScroll}>{searchElement}</div>
        )}
        {listContent}
      </div>
    </div>
  );
}

export default CheckboxGroup;
